import type { ErrorToastHandler } from './errorToastBridge';
import { createErrorToastBridge } from './errorToastBridge';

/**
 * Vue Query and the legacy API client report errors before the root toast UI
 * necessarily exists.
 *
 * Boot order in this app matters:
 * - main.ts installs Vue Query and creates API clients during application setup
 * - App.vue creates the toast provider later, inside the root component
 *
 * The bridge keeps a single public error-toast API for both systems and buffers
 * any early errors until App.vue registers the real UI handler.
 */
const errorToastBridge = createErrorToastBridge();

/**
 * Registers the real UI toast handler after App.vue creates the toast provider.
 */
export const setErrorToastHandler = (handler: ErrorToastHandler): void => {
  errorToastBridge.setErrorToastHandler(handler);
};

/**
 * Shared error-toast entrypoint for Vue Query, the legacy API client, and any
 * component code that needs to show a generic error message.
 */
export const showErrorToast = (message: string): void => {
  errorToastBridge.showErrorToast(message);
};

const CONNECTION_ERROR_MESSAGE = 'Could not reach the server. Reload the page to try again.';
const SESSION_REFRESH_FAILED_MESSAGE = 'Could not refresh your session. Please try again in a moment.';
const CONNECTION_ERROR_TOAST_WINDOW_MS = 2_000;

let connectionErrorToastShownAt = Number.NEGATIVE_INFINITY;

export const showConnectionErrorToast = (display: ErrorToastHandler = showErrorToast): void => {
  const now = performance.now();
  if (now - connectionErrorToastShownAt < CONNECTION_ERROR_TOAST_WINDOW_MS) {
    return;
  }
  connectionErrorToastShownAt = now;
  display(CONNECTION_ERROR_MESSAGE);
};

const hasHttpStatus = (error: unknown): boolean =>
  typeof error === 'object' && error !== null && 'status' in error && typeof error.status === 'number';

export const showRefreshFailedToast = (error: unknown): void => {
  if (hasHttpStatus(error)) {
    showErrorToast(SESSION_REFRESH_FAILED_MESSAGE);
    return;
  }
  showConnectionErrorToast();
};
